import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PlusIcon, SearchIcon, FilterIcon, ClockIcon } from 'lucide-react';
import PropertyCard from './common/PropertyCard';
import { mockProperties } from '../data/mockData';
import Button from './common/Button';
import { useFilter } from './context/FilterContext';
const PropertyList = () => {
  const {
    strategyFilter
  } = useFilter();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('All');
  const [localStrategyFilter, setLocalStrategyFilter] = useState('All');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  // Sync local filter with global filter
  useEffect(() => {
    setLocalStrategyFilter(strategyFilter);
  }, [strategyFilter]);
  const filteredProperties = mockProperties.filter(property => {
    const matchesSearch = property.name.toLowerCase().includes(searchTerm.toLowerCase()) || property.address.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesTypeFilter = filterType === 'All' || property.type === filterType;
    const matchesStrategyFilter = localStrategyFilter === 'All' || property.investmentStrategy === localStrategyFilter;
    return matchesSearch && matchesTypeFilter && matchesStrategyFilter;
  });
  // Determine badge color based on investment strategy
  const getStrategyBadgeColor = strategy => {
    switch (strategy) {
      case 'Long-Term Hold':
        return 'bg-blue-100 text-blue-800';
      case 'Short-Term Flip':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  return <div data-id="element-2513">
      <div className="flex justify-between items-center mb-6" data-id="element-2514">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-2515">Properties</h1>
        <Button data-id="element-2516">
          <PlusIcon className="w-5 h-5 mr-2" data-id="element-2517" />
          Add Property
        </Button>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col md:flex-row gap-4 mb-6" data-id="element-2518">
        <div className="relative flex-grow" data-id="element-2519">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2520">
            <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-2521" />
          </div>
          <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="Search properties..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-2522" />
        </div>
        <div className="relative w-full md:w-48" data-id="element-2523">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2524">
            <FilterIcon className="h-5 w-5 text-gray-400" data-id="element-2525" />
          </div>
          <select className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={filterType} onChange={e => setFilterType(e.target.value)} data-id="element-2526">
            <option value="All" data-id="element-2527">All Types</option>
            <option value="Residential" data-id="element-2528">Residential</option>
            <option value="Commercial" data-id="element-2529">Commercial</option>
            <option value="Industrial" data-id="element-2530">Industrial</option>
            <option value="Land" data-id="element-2531">Land</option>
          </select>
        </div>
        <div className="relative w-full md:w-48" data-id="element-2532">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2533">
            <ClockIcon className="h-5 w-5 text-gray-400" data-id="element-2534" />
          </div>
          <select className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={localStrategyFilter} onChange={e => setLocalStrategyFilter(e.target.value)} data-id="element-2535">
            <option value="All" data-id="element-2536">All Strategies</option>
            <option value="Long-Term Hold" data-id="element-2537">Long-Term Hold</option>
            <option value="Short-Term Flip" data-id="element-2538">Short-Term Flip</option>
          </select>
        </div>
      </div>

      {/* View mode toggle */}
      <div className="flex justify-between items-center mb-4" data-id="element-2539">
        <div className="text-sm text-gray-500" data-id="element-2540">
          {filteredProperties.length} properties
        </div>
        <div className="flex gap-2" data-id="element-2541">
          <button className={`p-1.5 rounded ${viewMode === 'grid' ? 'bg-gray-100 text-gray-800' : 'text-gray-500 hover:bg-gray-50'}`} onClick={() => setViewMode('grid')} data-id="element-2542">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-2543">
              <rect x="3" y="3" width="7" height="7" data-id="element-2544" />
              <rect x="14" y="3" width="7" height="7" data-id="element-2545" />
              <rect x="14" y="14" width="7" height="7" data-id="element-2546" />
              <rect x="3" y="14" width="7" height="7" data-id="element-2547" />
            </svg>
          </button>
          <button className={`p-1.5 rounded ${viewMode === 'list' ? 'bg-gray-100 text-gray-800' : 'text-gray-500 hover:bg-gray-50'}`} onClick={() => setViewMode('list')} data-id="element-2548">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-2549">
              <line x1="8" y1="6" x2="21" y2="6" data-id="element-2550" />
              <line x1="8" y1="12" x2="21" y2="12" data-id="element-2551" />
              <line x1="8" y1="18" x2="21" y2="18" data-id="element-2552" />
              <line x1="3" y1="6" x2="3.01" y2="6" data-id="element-2553" />
              <line x1="3" y1="12" x2="3.01" y2="12" data-id="element-2554" />
              <line x1="3" y1="18" x2="3.01" y2="18" data-id="element-2555" />
            </svg>
          </button>
        </div>
      </div>

      {/* Properties Grid or List */}
      {filteredProperties.length > 0 ? viewMode === 'grid' ? <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-2556">
            {filteredProperties.map(property => <PropertyCard key={property.id} property={property} data-id="element-2557" />)}
          </div> : <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-2558">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-2559">
              <thead className="bg-gray-50" data-id="element-2560">
                <tr data-id="element-2561">
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2562">
                    Property
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2563">
                    Type
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2564">
                    Size
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2565">
                    Value
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2566">
                    Strategy
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2567">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-2568">
                {filteredProperties.map(property => {
            const isFlip = property.investmentStrategy === 'Short-Term Flip';
            return <tr key={property.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => window.location.href = `/properties/${property.id}`} data-id="element-2569">
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-2570">
                        <div className="flex items-center" data-id="element-2571">
                          <div className="flex-shrink-0 h-10 w-10" data-id="element-2572">
                            <img className="h-10 w-10 rounded-md object-cover" src={property.imageUrl} alt={property.name} data-id="element-2573" />
                          </div>
                          <div className="ml-4" data-id="element-2574">
                            <div className="text-sm font-medium text-gray-900" data-id="element-2575">
                              {property.name}
                            </div>
                            <div className="text-sm text-gray-500 truncate max-w-xs" data-id="element-2576">
                              {property.address}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-2577">
                        <div className="text-sm text-gray-900" data-id="element-2578">
                          {property.type}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-2579">
                        <div className="text-sm text-gray-900" data-id="element-2580">
                          {property.size.toLocaleString()} sq ft
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-2581">
                        <div className="text-sm font-medium text-gray-900" data-id="element-2582">
                          ${property.value.toLocaleString()}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-2583">
                        <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${getStrategyBadgeColor(property.investmentStrategy)}`} data-id="element-2584">
                          {property.investmentStrategy}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-2585">
                        <div className="flex items-center" data-id="element-2586">
                          <div className="w-full bg-gray-200 rounded-full h-1.5 mr-2 max-w-[100px]" data-id="element-2587">
                            <div className={`h-1.5 rounded-full ${isFlip ? 'bg-purple-600' : 'bg-blue-600'}`} style={{
                      width: `${property.occupancy}%`
                    }} data-id="element-2588"></div>
                          </div>
                          <span className="text-sm text-gray-500" data-id="element-2589">
                            {property.occupancy}%{' '}
                            {isFlip ? 'Complete' : 'Occupied'}
                          </span>
                        </div>
                      </td>
                    </tr>;
          })}
              </tbody>
            </table>
          </div> : <div className="text-center py-12" data-id="element-2590">
          <p className="text-gray-500" data-id="element-2591">
            No properties found matching your criteria.
          </p>
        </div>}
    </div>;
};
export default PropertyList;